import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { downloadBase64Pdf } from '@/utils/pdfDownload';
import type {
  AddCollectionThreadCommentInput,
  CollectionThreadActivityDto,
  CollectionThreadListItemDto,
  CollectionThreadReminderTaskDto,
  CollectionThreadWithActivitiesDto,
  CreateCollectionThreadInput,
  BulkAssignCollectionThreadsInput,
  BulkAssignCollectionThreadsOutput,
  BulkResolveCollectionThreadsInput,
  BulkResolveCollectionThreadsOutput,
  DownloadCollectionThreadsPdfInput,
  DownloadCollectionThreadsPdfOutput,
  FileCollectionThreadEmailFileEvidenceInput,
  FileCollectionThreadEmailFileEvidenceOutput,
  InboxCustomerSummaryDto,
  LinkCollectionThreadApInvoiceEvidenceInput,
  LinkCollectionThreadApInvoiceEvidenceOutput,
  ListCollectionThreadEvidenceOutput,
  ListCollectionThreadsInput,
  ListCollectionThreadsOutput,
  ListInboxCustomersInput,
  ListRankedCollectionThreadsInput,
  RankedCollectionThreadDto,
  RefreshCollectionThreadEvidenceInput,
  RefreshCollectionThreadEvidenceOutput,
  ReopenCollectionThreadInput,
  ResolveCollectionThreadInput,
  SetCollectionThreadNextStepInput,
  UnlinkCollectionThreadApInvoiceEvidenceInput,
  UnlinkCollectionThreadApInvoiceEvidenceOutput,
  UpdateCollectionThreadInput,
} from '@erp/rpc';

/**
 * List collection threads with optional filters (customer, invoice, status, etc.).
 */
export function useArCollectionThreadsList(
  input: ListCollectionThreadsInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.list.queryOptions({ input }),
    enabled,
  });
  return {
    ...query,
    threads: query.data?.threads ?? [],
    total: query.data?.total ?? 0,
    // Segment counts for the attention control; only present when the request
    // carried an `attention` param.
    attentionCounts: query.data?.attentionCounts,
  } satisfies typeof query & {
    threads: CollectionThreadListItemDto[];
    total: number;
    attentionCounts: ListCollectionThreadsOutput['attentionCounts'];
  };
}

/**
 * List OPEN threads ranked by urgency (type severity + dollars at risk +
 * staleness). Powers the AR Summary "threads needing attention" card and the
 * Collections page Threads tab.
 */
export function useArCollectionThreadsRanked(
  input: ListRankedCollectionThreadsInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.listRanked.queryOptions({
      input,
    }),
    enabled,
  });
  return {
    ...query,
    threads: query.data?.threads ?? [],
  } satisfies typeof query & {
    threads: RankedCollectionThreadDto[];
  };
}

/**
 * Customers with open collection threads, aggregated for the inbox left rail
 * and the AR Summary mini card. Sorted oldest-open-first.
 */
export function useArCollectionInboxCustomers(
  input: ListInboxCustomersInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.listInboxCustomers.queryOptions(
      { input },
    ),
    enabled,
  });
  return {
    ...query,
    customers: query.data?.customers ?? [],
  } satisfies typeof query & {
    customers: InboxCustomerSummaryDto[];
  };
}

/**
 * Fetch a single thread with its full activity feed.
 */
export function useArCollectionThread(id: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: id !== null,
  });
  return {
    ...query,
    thread: query.data ?? null,
  } satisfies typeof query & {
    thread: CollectionThreadWithActivitiesDto | null;
  };
}

/**
 * The outstanding AR reminder task pointing at this thread, if any — powers
 * the "why the system wants you here" banner pinned above the thread.
 *
 * Kept separate from `useArCollectionThread` so completing the task refreshes
 * only the banner (via `invalidateTaskQueries`) instead of refetching the whole
 * thread + activity feed.
 */
export function useArCollectionThreadReminderTask(id: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.getReminderTask.queryOptions(
      { input: { id: id ?? '' } },
    ),
    enabled: id !== null,
  });
  return {
    ...query,
    reminderTask: query.data?.task ?? null,
  } satisfies typeof query & {
    reminderTask: CollectionThreadReminderTaskDto | null;
  };
}

/**
 * Refetches every collection-thread read (lists, inbox, detail, evidence,
 * document names). Exported for mutations that live outside this hook file
 * but still change thread state — e.g. sending an email draft, which appends
 * a thread activity server-side.
 */
export function invalidateThreadQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.listRanked.key(),
  });
  void queryClient.invalidateQueries({
    queryKey:
      orpc.accountsReceivable.arCollectionThreads.listInboxCustomers.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.get.key(),
  });
  // Evidence is derived from the thread's invoice link, so any mutation
  // can shift it (in particular: invoice-id changes on update).
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.listEvidence.key(),
  });
  // Document names track the evidence document set, so refresh them alongside
  // evidence — otherwise generated labels stay stale until a hard reload.
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCollectionThreads.documentNames.key(),
  });
}

export function useCreateArCollectionThread() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.create.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadWithActivitiesDto,
      Error,
      CreateCollectionThreadInput
    >
  >;
}

export function useUpdateArCollectionThread() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.update.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadWithActivitiesDto,
      Error,
      UpdateCollectionThreadInput
    >
  >;
}

export function useResolveArCollectionThread() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.resolve.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadWithActivitiesDto,
      Error,
      ResolveCollectionThreadInput
    >
  >;
}

export function useBulkResolveArCollectionThreads() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.bulkResolve.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      BulkResolveCollectionThreadsOutput,
      Error,
      BulkResolveCollectionThreadsInput
    >
  >;
}

export function useBulkAssignArCollectionThreads() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.bulkAssign.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      BulkAssignCollectionThreadsOutput,
      Error,
      BulkAssignCollectionThreadsInput
    >
  >;
}

/**
 * Generates a PDF of the given threads (mirroring the caller's visible column
 * layout), decodes the base64 response, and triggers a browser download.
 */
export function useDownloadArCollectionThreadsPdf() {
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.downloadPdf.mutationOptions({
      onSuccess: (data) => downloadBase64Pdf(data.pdf, data.filename),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      DownloadCollectionThreadsPdfOutput,
      Error,
      DownloadCollectionThreadsPdfInput
    >
  >;
}

export function useReopenArCollectionThread() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.reopen.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadWithActivitiesDto,
      Error,
      ReopenCollectionThreadInput
    >
  >;
}

/**
 * Record the rep's next step on a thread: waiting-on reason + follow-up date +
 * note, written atomically (thread columns + a `next_step` timeline activity).
 * Deliberately quiet server-side — no watcher notification fan-out.
 */
export function useSetArCollectionThreadNextStep() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.setNextStep.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadWithActivitiesDto,
      Error,
      SetCollectionThreadNextStepInput
    >
  >;
}

export function useAddArCollectionThreadComment() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.addComment.mutationOptions({
      onSuccess: () => invalidateThreadQueries(queryClient),
    }),
  ) satisfies ReturnType<
    typeof useMutation<
      CollectionThreadActivityDto,
      Error,
      AddCollectionThreadCommentInput
    >
  >;
}

/**
 * File an already-uploaded document as evidence on a thread. The document must
 * first be uploaded against the thread (entityType `ar_collection_thread`) via
 * useUploadEntityDocument; this links it into the evidence graph and records a
 * timeline activity. Invalidates thread + evidence queries on success.
 */
export function useFileArCollectionThreadDocumentEvidence() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.fileDocumentEvidence.mutationOptions(
      {
        onSuccess: () => invalidateThreadQueries(queryClient),
      },
    ),
  ) satisfies ReturnType<
    typeof useMutation<
      { filed: boolean },
      Error,
      { id: string; documentId: string }
    >
  >;
}

/**
 * Parse an already-uploaded `.eml`/`.msg` document into a real InboundEmail and
 * file it as EMAIL evidence on a thread. Same upload precondition as
 * useFileArCollectionThreadDocumentEvidence; the server re-sniffs the content
 * and rejects non-email files. Idempotent: `filed: false` means the email was
 * already on the thread (surface as an info toast, not an error). Invalidates
 * thread + evidence queries on success.
 */
export function useFileArCollectionThreadEmailFileEvidence() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.fileEmailFileAsEvidence.mutationOptions(
      {
        onSuccess: () => invalidateThreadQueries(queryClient),
      },
    ),
  ) satisfies ReturnType<
    typeof useMutation<
      FileCollectionThreadEmailFileEvidenceOutput,
      Error,
      FileCollectionThreadEmailFileEvidenceInput
    >
  >;
}

/**
 * Manually link an existing AP (vendor/carrier) invoice as evidence on a
 * thread. Links the invoice plus its supporting documents (BOL/POD/etc.) into
 * the evidence graph and records a timeline activity. Idempotent on the server
 * (already-linked → no-op). Invalidates thread + evidence queries on success.
 */
export function useLinkArCollectionThreadApInvoiceEvidence() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.linkApInvoiceEvidence.mutationOptions(
      {
        onSuccess: () => invalidateThreadQueries(queryClient),
      },
    ),
  ) satisfies ReturnType<
    typeof useMutation<
      LinkCollectionThreadApInvoiceEvidenceOutput,
      Error,
      LinkCollectionThreadApInvoiceEvidenceInput
    >
  >;
}

/**
 * Remove a manually-linked AP invoice (and the supporting documents the manual
 * link brought along) from a thread's evidence. Only affects manually-filed
 * associations; gather-derived evidence is left intact.
 */
export function useUnlinkArCollectionThreadApInvoiceEvidence() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.unlinkApInvoiceEvidence.mutationOptions(
      {
        onSuccess: () => invalidateThreadQueries(queryClient),
      },
    ),
  ) satisfies ReturnType<
    typeof useMutation<
      UnlinkCollectionThreadApInvoiceEvidenceOutput,
      Error,
      UnlinkCollectionThreadApInvoiceEvidenceInput
    >
  >;
}

/**
 * Re-runs the evidence gather for a thread (the same walk that fires on thread
 * creation). Idempotent — picks up evidence that landed after the thread was
 * opened, including AP invoices newly matched by reference_ids. Invalidates
 * thread + evidence queries on success.
 */
export function useRefreshArCollectionThreadEvidence() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCollectionThreads.refreshEvidence.mutationOptions(
      {
        onSuccess: () => invalidateThreadQueries(queryClient),
      },
    ),
  ) satisfies ReturnType<
    typeof useMutation<
      RefreshCollectionThreadEvidenceOutput,
      Error,
      RefreshCollectionThreadEvidenceInput
    >
  >;
}

const EMPTY_EVIDENCE: ListCollectionThreadEvidenceOutput = {
  paymentApplications: [],
  receipts: [],
  remittanceAdvices: [],
  remittanceLineItems: [],
  inboundEmails: [],
  outboundEmails: [],
  documents: [],
  shipments: [],
  invoice: null,
  salesOrder: null,
  customerPurchaseOrder: null,
  apInvoices: [],
};

/**
 * Fetch evidence (invoices, payments, remittance advices, shipments, docs)
 * linked to a thread. Backend auto-derives this on thread creation when the
 * thread points at an invoice; the response may briefly be empty if read
 * runs before the post-commit gather hook completes.
 */
export function useArCollectionThreadEvidence(id: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.listEvidence.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: id !== null,
  });
  return {
    ...query,
    evidence: query.data ?? EMPTY_EVIDENCE,
  } satisfies typeof query & {
    evidence: ListCollectionThreadEvidenceOutput;
  };
}

/**
 * Fetch LLM-generated readable names for a thread's evidence documents, keyed
 * by documentId. Separate from useArCollectionThreadEvidence so the evidence
 * list renders immediately while names — which may need a cheap LLM call on a
 * cold cache — resolve in a second request. Names are cached server-side, so
 * subsequent loads return instantly. Documents without a resolved name are
 * simply absent from the map (the UI falls back to classification/filename).
 */
export function useArCollectionThreadDocumentNames(id: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionThreads.documentNames.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: id !== null,
  });
  const namesByDocumentId = useMemo(
    () =>
      new Map(
        (query.data?.names ?? []).map((n) => [n.documentId, n.displayName]),
      ),
    [query.data],
  );
  return { ...query, namesByDocumentId };
}
